import {
  AppOverridesContext,
  type AppOverrides,
} from 'contexts/AppOverridesContext';
import {AccessibilityProvider} from 'contexts/accessibility/AccessibilityContext';
import {ColorSchemeProvider} from 'contexts/ColorSchemeContext';
import {RoadmapStyleProvider} from 'contexts/RoadmapStyleContext';
import {UiPreferencesProvider} from 'contexts/UiPreferencesContext';
import {DeveloperToolsProvider} from 'contexts/DeveloperToolsContext';
import {EnrollmentProvider} from 'contexts/EnrollmentContext';
import {LoadingProvider} from 'contexts/LoadingContext';
import {NavigationHistoryProvider} from 'contexts/NavigationContext';
import {TimeProvider} from 'contexts/TimeContext';
import {TranslationProvider} from 'contexts/translation/TranslationContext';
import {UiTrainingProvider} from 'contexts/UiTrainingContext';
import {Router} from './Router';
import {MaintenanceNotice} from 'components/maintenance/MaintenanceNotice';

// Registers the KaTeX `@font-face` rules (for `KaTeX_Main`) once for the whole
// app, so every route — the PAI tool demos, the slide component embeds, and
// mathide — gets the fonts without each one re-importing the stylesheet.
import 'katex/dist/katex.min.css';

/** Props for the top-level {@link App} component. */
interface AppProps {
  /**
   * Optional component overrides. When a key is provided its value replaces
   * the corresponding CIP default component. Omitting the prop (or any key
   * within it) preserves the original CIP behaviour unchanged.
   *
   * Currently supported keys:
   * - `SplashPage` — the landing page rendered at the index route (`/`).
   * - `ToolRoutes` — nested routes mounted at `/:courseId/tools/*`.
   *
   * @example PAI wrapper supplying a custom landing page:
   * ```tsx
   * import PaiSplashPage from './PaiSplashPage';
   * <App components={{ SplashPage: PaiSplashPage }} />
   * ```
   */
  components?: AppOverrides;
}

/**
 * Top-level CIP application component.
 *
 * Composes all context providers and the router in one place. The optional
 * `components` prop is the extension point for external deploy wrappers (e.g.
 * PAI): pass overrides to substitute specific UI components without forking or
 * patching CIP source files.
 *
 * CIP's own entrypoint (`index.tsx`) renders `<App />` with no props, which
 * preserves all existing behaviour exactly as before.
 *
 * @param props.components - Optional map of component overrides. Defaults to
 *   `{}` (no overrides) when omitted, keeping CIP defaults for every slot.
 */
export function App({components = {}}: AppProps = {}) {
  return (
    <AppOverridesContext.Provider value={components}>
      <DeveloperToolsProvider>
        <ColorSchemeProvider>
          <UiPreferencesProvider>
            <RoadmapStyleProvider>
              <TranslationProvider>
              <LoadingProvider>
                <NavigationHistoryProvider>
                  <TimeProvider>
                    <AccessibilityProvider>
                      <EnrollmentProvider>
                        <UiTrainingProvider>
                          <MaintenanceNotice />
                          <Router />
                        </UiTrainingProvider>
                      </EnrollmentProvider>
                    </AccessibilityProvider>
                  </TimeProvider>
                </NavigationHistoryProvider>
              </LoadingProvider>
              </TranslationProvider>
            </RoadmapStyleProvider>
          </UiPreferencesProvider>
        </ColorSchemeProvider>
      </DeveloperToolsProvider>
    </AppOverridesContext.Provider>
  );
}
